import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Pencil, ReceiptText, RefreshCw, Ticket, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Modal from '../../components/modal/Modal';
import ModalRefundAndUpgrade from '../../components/MemberProfile/ModalRefund';
import { getMembershipForMember } from '../../api/memberShip';
import { convertToKRW, getItemWithExpiration } from '../../lib/utils';
import { MemberMembershipDataType } from '../../services/useMembershipQueryService';
import { isActionableMembershipDetail, normalizeMembershipDetail } from '../../lib/membershipDetailGuard';
import { MembershipPaymentData, SalesManager, SalesStatus } from '../../services/useMembershipPaymentsQueryService';
import {
    formatSalesRecordDate,
    getSalesManagerEditBlockMessage,
    getSalesRecordRefundableAmount,
    getSalesRefundAvailability,
    hasAuthoritativeCurrentMembership,
    resolveSalesMembershipId,
    resolveSalesRecordId,
} from './salesRecordDetail';
import SalesManagerCorrectionModal from './SalesManagerCorrectionModal';

type Props = {
    record: MembershipPaymentData;
    onClose: () => void;
    createAlert?: (title: string, content: string, isError: boolean) => void;
    onRefetchSalesRecord: () => Promise<MembershipPaymentData>;
};

const statusMeta: Record<SalesStatus, { label: string; color: string; background: string }> = {
    COMPLETED: { label: '결제 완료', color: '#166534', background: '#DCFCE7' },
    PARTIAL_PAYMENT: {
        label: '분할 결제',
        color: '#047857',
        background: '#D1FAE5',
    },
    UPGRADED: { label: '업그레이드', color: '#7E22CE', background: '#F3E8FF' },
    PARTIAL_REFUND: {
        label: '부분 환불',
        color: '#C2410C',
        background: '#FFEDD5',
    },
    FULL_REFUND: { label: '전체 환불', color: '#B91C1C', background: '#FEE2E2' },
};

const valueOrDash = (value?: string | number | null) => (value === undefined || value === null || value === '' ? '-' : String(value));

const sessionText = (current?: number, total?: number) => {
    if (!Number.isFinite(Number(current)) || !Number.isFinite(Number(total))) return '-';
    return `${Number(current)}/${Number(total)}회`;
};

const infoLabelStyle: React.CSSProperties = {
    fontSize: 13,
    lineHeight: '19px',
    color: '#6B7280',
    marginBottom: 6,
};

const infoValueStyle: React.CSSProperties = {
    fontSize: 15,
    lineHeight: '22px',
    fontWeight: 600,
    color: '#111827',
    wordBreak: 'break-word',
};

const InfoItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div>
        <div style={infoLabelStyle}>{label}</div>
        <div style={infoValueStyle}>{value}</div>
    </div>
);

const SalesRecordDetailModal = ({ record, onClose, createAlert, onRefetchSalesRecord }: Props) => {
    const navigate = useNavigate();
    const [isRefundOpen, setIsRefundOpen] = useState(false);
    const [isSalesManagerCorrectionOpen, setIsSalesManagerCorrectionOpen] = useState(false);
    const [correctedSalesManager, setCorrectedSalesManager] = useState<SalesManager>();
    const authorization = getItemWithExpiration('authorization');
    const centerId = getItemWithExpiration('authObject')?.centerId;
    const source: 'pt' | 'group' = record.source === 'group' ? 'group' : 'pt';
    const membershipId = resolveSalesMembershipId(record);
    const hasAuthoritativeMembershipLink = hasAuthoritativeCurrentMembership(record);
    const showAlert =
        createAlert ||
        ((title: string, content: string) => {
            window.alert(`${title}\n${content}`);
        });

    const membershipQuery = useQuery<MemberMembershipDataType>({
        queryKey: ['salesRecordMembershipDetail', source, centerId, membershipId],
        queryFn: async () => {
            if (!membershipId) throw new Error('MEMBERSHIP_ID_MISSING');
            const response = await getMembershipForMember(membershipId, authorization, source, centerId);
            const membership = normalizeMembershipDetail(response?.data?.membership);
            if (response?.status !== 200 || !membership || Array.isArray(membership) || !isActionableMembershipDetail(membership)) {
                throw new Error(`MEMBERSHIP_NOT_RESOLVED_${response?.status ?? 'UNKNOWN'}`);
            }
            return { ...membership, source } as MemberMembershipDataType;
        },
        enabled:
            hasAuthoritativeMembershipLink &&
            !!authorization &&
            !!membershipId &&
            (source === 'pt' || !!centerId),
        suspense: false,
        retry: false,
    });

    const membership = membershipQuery.data;
    const hasResolvedMembership = isActionableMembershipDetail(membership);
    const refundableAmount = getSalesRecordRefundableAmount(record);
    const refundAvailability = getSalesRefundAvailability(record, hasResolvedMembership);
    const salesRecordId = resolveSalesRecordId(record);
    const status = statusMeta[record.saleStatus] || statusMeta.COMPLETED;

    const originalCoachName = record.coachName || '-';
    const currentCoachName =
        (hasAuthoritativeMembershipLink ? membership?.coachName : undefined) || record.currentCoachName || '-';
    const currentCoachId = (hasAuthoritativeMembershipLink ? membership?.coachId : undefined) || record.currentCoachId;
    const hasHandoff = useMemo(() => {
        if (record.currentMembershipId && record.currentMembershipId !== record.membershipId) return true;
        if (record.currentMemberTicketId && record.currentMemberTicketId !== record.memberTicketId) return true;
        if (record.coachId && currentCoachId) return record.coachId !== currentCoachId;
        return !!(hasResolvedMembership && originalCoachName !== '-' && currentCoachName !== '-' && originalCoachName !== currentCoachName);
    }, [currentCoachId, currentCoachName, hasResolvedMembership, originalCoachName, record]);

    const displayedSalesManager = correctedSalesManager ?? record.salesManager;
    const salesManagerName = displayedSalesManager?.userName || '판매 담당자 미확인';
    const showSalesManagerCorrectionEntry = source === 'pt' && record.salesManagerEditable !== undefined;
    const hasSalesRecordId = !!resolveSalesRecordId(record);
    const salesManagerCorrectionDisabled = record.salesManagerEditable !== true || !hasSalesRecordId;
    const salesManagerCorrectionBlockMessage = !hasSalesRecordId
        ? '결제 식별자를 확인할 수 없어 판매자를 수정할 수 없어요.'
        : getSalesManagerEditBlockMessage(record.salesManagerEditBlockReason);
    const remainingSession = membership?.remainSession;

    const openMembershipDetail = () => {
        if (!hasAuthoritativeMembershipLink || !membership?._id) return;
        onClose();
        navigate('/member/detail/membership', {
            state: {
                membershipId: membership._id,
                memberName: membership.memberName || record.memberName,
                coachName: currentCoachName === '-' ? record.coachName : currentCoachName,
                memberId: membership.memberId || record.memberId,
                coachId: membership.coachId || record.currentCoachId || record.coachId,
                source,
                centerId,
            },
        });
    };

    return (
        <>
            <Modal whiteViewWidth={960} whiteViewHeight={760} borderRadius={18} isCloseButton isCloseOutside={false} onClose={onClose}>
                <div className="min-h-full bg-[#F8FAFC] px-[40px] pb-[40px] pt-[36px]">
                    <div className="mb-[28px] flex items-start justify-between pr-[48px]">
                        <div>
                            <div className="mb-[8px] flex items-center gap-[10px]">
                                <ReceiptText size={24} color="#1F2937" />
                                <h2 className="text-[24px] font-bold leading-[34px] text-[#111827]">매출 상세</h2>
                                <span
                                    className="rounded-full px-[10px] py-[4px] text-[13px] font-semibold"
                                    style={{
                                        color: status.color,
                                        backgroundColor: status.background,
                                    }}
                                >
                                    {status.label}
                                </span>
                            </div>
                            <p className="text-[14px] leading-[21px] text-[#6B7280]">
                                매출 귀속과 현재 수강권을 분리해 확인할 수 있습니다.
                            </p>
                        </div>
                        <div className="text-right">
                            <div className="mb-[4px] text-[13px] text-[#6B7280]">환불 가능 금액</div>
                            <div className="text-[26px] font-bold leading-[34px] text-[#111827]">
                                {refundableAmount === null ? '서버 확인 필요' : `${convertToKRW(refundableAmount)}원`}
                            </div>
                        </div>
                    </div>

                    <section className="mb-[20px] rounded-[16px] border border-solid border-[#E5E7EB] bg-white p-[24px]">
                        <div className="mb-[18px] flex items-center gap-[8px]">
                            <ReceiptText size={18} color="#374151" />
                            <h3 className="text-[17px] font-bold text-[#1F2937]">원매출 정보</h3>
                        </div>
                        <div className="grid grid-cols-4 gap-x-[24px] gap-y-[20px]">
                            <InfoItem label="회원" value={valueOrDash(record.memberName)} />
                            <InfoItem label="수강권" value={valueOrDash(record.membershipName)} />
                            <InfoItem label="결제일" value={formatSalesRecordDate(record.paymentDate)} />
                            <InfoItem label="구분" value={source === 'group' ? '그룹' : 'PT'} />
                            <InfoItem
                                label="판매 담당자 (매출 귀속)"
                                value={
                                    <div>
                                        <div className="flex items-center gap-[8px]">
                                            <span>{salesManagerName}</span>
                                            {showSalesManagerCorrectionEntry && (
                                                <button
                                                    type="button"
                                                    disabled={salesManagerCorrectionDisabled}
                                                    onClick={() => setIsSalesManagerCorrectionOpen(true)}
                                                    className="inline-flex h-[28px] items-center gap-[4px] rounded-[7px] border border-solid border-[#C7D2FE] bg-[#EEF2FF] px-[8px] text-[13px] font-semibold text-[#4338CA] hover:bg-[#E0E7FF] disabled:cursor-not-allowed disabled:border-[#E5E7EB] disabled:bg-[#F3F4F6] disabled:text-[#6B7280]"
                                                >
                                                    <Pencil size={12} /> 판매자 수정
                                                </button>
                                            )}
                                        </div>
                                        {showSalesManagerCorrectionEntry && salesManagerCorrectionDisabled && (
                                            <div className="mt-[5px] max-w-[205px] text-[13px] font-normal leading-[16px] text-[#9A3412]">
                                                {salesManagerCorrectionBlockMessage}
                                            </div>
                                        )}
                                    </div>
                                }
                            />
                            <InfoItem label="판매 당시 담당 강사" value={valueOrDash(originalCoachName)} />
                            <InfoItem label="결제 금액" value={`${convertToKRW(Number(record.paymentAmount) || 0)}원`} />
                            <InfoItem label="취소 금액" value={`${convertToKRW(Number(record.cancelAmount) || 0)}원`} />
                        </div>
                    </section>

                    {hasHandoff && (
                        <div className="mb-[20px] flex gap-[12px] rounded-[14px] border border-solid border-[#BFDBFE] bg-[#EFF6FF] px-[18px] py-[15px]">
                            <ArrowRight className="mt-[1px] shrink-0" size={19} color="#2563EB" />
                            <div>
                                <div className="mb-[3px] text-[14px] font-bold text-[#1E3A8A]">인계된 수강권입니다</div>
                                <div className="text-[13px] leading-[20px] text-[#1E40AF]">
                                    환불액은 {salesManagerName}님의 환불 발생월 매출에서 차감되고, 회차는 현재 담당 강사의 수강권에서
                                    차감됩니다.
                                </div>
                            </div>
                        </div>
                    )}

                    <section className="mb-[24px] rounded-[16px] border border-solid border-[#E5E7EB] bg-white p-[24px]">
                        <div className="mb-[18px] flex items-center justify-between">
                            <div className="flex items-center gap-[8px]">
                                <Ticket size={18} color="#374151" />
                                <h3 className="text-[17px] font-bold text-[#1F2937]">현재 수강권</h3>
                            </div>
                            {hasAuthoritativeMembershipLink && hasResolvedMembership && (
                                <span className="rounded-full bg-[#ECFDF5] px-[10px] py-[4px] text-[13px] font-semibold text-[#047857]">
                                    연결 확인
                                </span>
                            )}
                        </div>

                        {!hasAuthoritativeMembershipLink ? (
                            <div className="flex min-h-[108px] items-center gap-[12px] rounded-[12px] bg-[#FFF7ED] px-[18px] py-[16px]">
                                <AlertTriangle className="shrink-0" size={19} color="#C2410C" />
                                <div>
                                    <div className="mb-[4px] text-[14px] font-bold text-[#9A3412]">
                                        서버에서 현재 수강권 연결을 제공하지 않았습니다
                                    </div>
                                    <div className="text-[13px] leading-[20px] text-[#C2410C]">
                                        과거 수강권을 현재 수강권으로 간주하지 않으며, 연결 정보가 내려올 때까지 상세 이동과 환불을 잠급니다.
                                    </div>
                                </div>
                            </div>
                        ) : membershipQuery.isLoading ? (
                            <div className="flex h-[108px] items-center justify-center text-[14px] text-[#6B7280]">
                                현재 수강권 연결을 확인하고 있습니다.
                            </div>
                        ) : !hasResolvedMembership ? (
                            <div className="flex min-h-[108px] items-center justify-between gap-[20px] rounded-[12px] bg-[#FFF7ED] px-[18px] py-[16px]">
                                <div className="flex gap-[12px]">
                                    <AlertTriangle className="mt-[2px] shrink-0" size={19} color="#C2410C" />
                                    <div>
                                        <div className="mb-[4px] text-[14px] font-bold text-[#9A3412]">
                                            현재 수강권 연결을 확인할 수 없습니다
                                        </div>
                                        <div className="max-w-[590px] text-[13px] leading-[20px] text-[#C2410C]">
                                            인계 전 수강권을 가리키는 과거 매출일 수 있습니다. 다른 수강권을 임의로 추측하지 않으며, 연결이
                                            확인될 때까지 환불을 잠급니다.
                                        </div>
                                    </div>
                                </div>
                                {!!membershipId && (
                                    <button
                                        type="button"
                                        className="flex shrink-0 items-center gap-[6px] rounded-[8px] border border-solid border-[#FDBA74] bg-white px-[12px] py-[9px] text-[13px] font-semibold text-[#9A3412] hover:bg-[#FFF7ED]"
                                        onClick={() => membershipQuery.refetch()}
                                    >
                                        <RefreshCw size={14} /> 다시 확인
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-4 gap-x-[24px] gap-y-[20px]">
                                <InfoItem label="현재 수강권" value={valueOrDash(membership?.name)} />
                                <InfoItem
                                    label="현재 담당 강사"
                                    value={
                                        <span className="inline-flex items-center gap-[5px]">
                                            <UserRound size={14} color="#4B5563" /> {currentCoachName}
                                        </span>
                                    }
                                />
                                <InfoItem label="현재/총 회차" value={sessionText(membership?.currentSession, membership?.totalSession)} />
                                <InfoItem
                                    label="남은 회차"
                                    value={Number.isFinite(Number(remainingSession)) ? `${Number(remainingSession)}회` : '-'}
                                />
                                <InfoItem label="상태" value={membership?.status?.toLowerCase() === 'active' ? '활성' : '비활성'} />
                                <InfoItem label="시작일" value={formatSalesRecordDate(membership?.activationDate)} />
                                <InfoItem label="종료일" value={formatSalesRecordDate(membership?.expirationDate)} />
                            </div>
                        )}
                    </section>

                    <div className="flex items-center justify-between gap-[20px]">
                        <div className="max-w-[540px] text-[13px] leading-[20px] text-[#6B7280]">
                            {!refundAvailability.canRefund
                                ? refundAvailability.reason
                                : `${salesManagerName}님의 매출 귀속은 유지되며, 환불 회차만 현재 수강권에 반영됩니다.`}
                        </div>
                        <div className="flex gap-[10px]">
                            <button
                                type="button"
                                disabled={!hasAuthoritativeMembershipLink || !hasResolvedMembership}
                                className="h-[44px] rounded-[9px] border border-solid border-[#D1D5DB] bg-white px-[18px] text-[14px] font-semibold text-[#374151] hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-45"
                                onClick={openMembershipDetail}
                            >
                                현재 수강권 보기
                            </button>
                            <button
                                type="button"
                                disabled={!refundAvailability.canRefund}
                                className="h-[44px] rounded-[9px] bg-[#DC2626] px-[24px] text-[14px] font-bold text-white hover:bg-[#B91C1C] disabled:cursor-not-allowed disabled:bg-[#D1D5DB]"
                                onClick={() => refundAvailability.canRefund && setIsRefundOpen(true)}
                            >
                                환불 진행
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {isRefundOpen && membership && refundAvailability.canRefund && refundableAmount !== null && salesRecordId && (
                <ModalRefundAndUpgrade
                    setIsVisible={setIsRefundOpen}
                    membership={membership}
                    coachName={currentCoachName === '-' ? record.coachName : currentCoachName}
                    memberName={membership.memberName || record.memberName}
                    type="refund"
                    createAlert={showAlert}
                    maxRefundAmountOverride={refundableAmount}
                    salesAttributionName={salesManagerName}
                    salesRecordId={salesRecordId}
                    onRefundSuccess={({ hasActiveSharePoolWarning, isReplayed }) => {
                        onClose();
                        // 재생된 응답에는 모달이 "이미 처리된 요청" 안내를 이미 띄웠다.
                        // 여기서 성공 토스트를 덮어쓰면 이중 환불을 성공으로 숨기게 된다.
                        if (!hasActiveSharePoolWarning && !isReplayed) {
                            showAlert('환불 처리가 완료되었습니다', `${salesManagerName}님의 매출에 환불 금액이 반영되었습니다.`, false);
                        }
                    }}
                />
            )}

            {isSalesManagerCorrectionOpen && (
                <SalesManagerCorrectionModal
                    record={{ ...record, salesManager: displayedSalesManager }}
                    onClose={() => setIsSalesManagerCorrectionOpen(false)}
                    onUpdated={setCorrectedSalesManager}
                    onRequestListRefresh={onClose}
                    onRefetchSalesRecord={onRefetchSalesRecord}
                />
            )}
        </>
    );
};

export default SalesRecordDetailModal;
